// The add/edit form for a deliverable.
//
// The owner controls change shape with `kind`: team work takes a lead and a second,
// individual work and exams take a list of everyone who has to do it. People not
// enrolled in the selected class are disabled and silently dropped from any selection.

import { CLASSES, NOT_ENROLLED, STATUS_LABEL, STATUS_ORDER } from "../data/classes.js";
import { PEOPLE } from "../data/people.js";
import { iso, today } from "../lib/dates.js";
import { el, esc } from "../lib/dom.js";

const BLANK = {
  cls: "15.515",
  title: "",
  kind: "group",
  due: null, // filled at call time
  dueTime: "",
  owners: [],
  lead: "",
  second: "",
  effort: "M",
  note: "",
  status: "todo",
  window: "",
};

/**
 * @param {object|null} src   existing item, or null to add
 * @param {HTMLElement} host  where to mount
 * @param {(result: object|null) => void} onDone
 *        null = cancelled · { __delete: true, id } = delete · otherwise the saved item
 */
export function editorForm(src, host, onDone) {
  const isNew = !src;
  const m = { ...BLANK, due: iso(today()), ...(src || {}) };

  const f = el("div", "editor");
  f.innerHTML = `
    <div class="frow">
      <div class="field"><label for="e-cls">Class</label><select id="e-cls">
        ${CLASSES.map((c) => `<option value="${c.id}"${c.id === m.cls ? " selected" : ""}>${c.id} ${esc(c.name)}</option>`).join("")}
      </select></div>
      <div class="field grow"><label for="e-title">What is it</label>
        <input id="e-title" type="text" value="${esc(m.title)}" placeholder="e.g. HW #7 — consolidated statements"></div>
      <div class="field"><label for="e-kind">Type</label><select id="e-kind">
        <option value="group"${m.kind === "group" ? " selected" : ""}>Team</option>
        <option value="individual"${m.kind === "individual" ? " selected" : ""}>Individual</option>
        <option value="exam"${m.kind === "exam" ? " selected" : ""}>Exam</option>
      </select></div>
    </div>
    <div class="frow">
      <div class="field"><label for="e-due">Due date</label><input id="e-due" type="date" value="${esc(m.due)}"></div>
      <div class="field"><label for="e-time">Time</label>
        <input id="e-time" type="text" size="8" value="${esc(m.dueTime || "")}" placeholder="4pm"></div>
      <div class="field"><label for="e-eff">Effort</label><select id="e-eff">
        ${["S", "M", "L"].map((x) => `<option value="${x}"${m.effort === x ? " selected" : ""}>${x === "S" ? "Small" : x === "M" ? "Medium" : "Large"}</option>`).join("")}
      </select></div>
      <div class="field"><label for="e-status">Status</label><select id="e-status">
        ${STATUS_ORDER.map((s) => `<option value="${s}"${m.status === s ? " selected" : ""}>${STATUS_LABEL[s]}</option>`).join("")}
      </select></div>
    </div>
    <div class="field" id="e-ownwrap"></div>
    <div class="field grow"><label for="e-note">Notes</label>
      <input id="e-note" type="text" value="${esc(m.note || "")}" placeholder="Link, room, format, who is drafting…"></div>
    <div class="frow" style="justify-content:flex-end">
      ${isNew ? "" : '<button class="btn ghost" id="e-del" style="margin-right:auto;color:var(--critical);border-color:color-mix(in srgb,var(--critical) 40%,transparent)">Delete</button>'}
      <button class="btn ghost" id="e-cancel">Cancel</button>
      <button class="btn" id="e-save">${isNew ? "Add it" : "Save"}</button>
    </div>`;
  host.appendChild(f);

  const owners = new Set(m.owners || []);
  let lead = m.lead || "";
  let second = m.second || "";
  const ownWrap = f.querySelector("#e-ownwrap");

  function drawOwners() {
    const cls = f.querySelector("#e-cls").value;
    const kind = f.querySelector("#e-kind").value;
    const excluded = NOT_ENROLLED[cls] || [];
    const pool = PEOPLE.filter((p) => !excluded.includes(p.id));
    if (excluded.includes(lead)) lead = "";
    if (excluded.includes(second)) second = "";

    if (kind === "group") {
      const opts = (sel) =>
        pool.map((p) => `<option value="${p.id}"${sel === p.id ? " selected" : ""}>${esc(p.name)}</option>`).join("");
      ownWrap.innerHTML = `
        <div class="frow" style="gap:12px;align-items:flex-end">
          <div class="field"><label for="e-lead" style="color:var(--brand)">Lead owner</label>
            <select id="e-lead"><option value="">— pick a lead —</option>${opts(lead)}</select></div>
          <div class="field"><label for="e-second">Second owner</label>
            <select id="e-second"><option value="">— none yet —</option>${opts(second)}</select></div>
          <span style="font-size:11.5px;color:var(--ink-3);padding-bottom:7px">Lead carries 70% of the effort weight, second 30%.</span>
        </div>`;
      ownWrap.querySelector("#e-lead").onchange = function () {
        lead = this.value;
        if (second && second === lead) second = "";
        drawOwners();
      };
      ownWrap.querySelector("#e-second").onchange = function () {
        second = this.value;
        if (lead && lead === second) lead = "";
        drawOwners();
      };
    } else {
      ownWrap.innerHTML = '<label>Who has to do it</label><div class="ownerpick" id="e-owners"></div>';
      const pick = ownWrap.querySelector("#e-owners");
      PEOPLE.forEach((p) => {
        const off = excluded.includes(p.id);
        const b = el("button", null, esc(p.short));
        b.type = "button";
        b.disabled = off;
        if (off) {
          b.title = `${p.short} isn’t in ${cls}`;
          owners.delete(p.id);
        }
        b.setAttribute("aria-pressed", owners.has(p.id) ? "true" : "false");
        b.onclick = () => {
          owners.has(p.id) ? owners.delete(p.id) : owners.add(p.id);
          drawOwners();
        };
        pick.appendChild(b);
      });
    }
  }

  drawOwners();
  f.querySelector("#e-cls").onchange = drawOwners;
  f.querySelector("#e-kind").onchange = drawOwners;
  f.querySelector("#e-cancel").onclick = () => onDone(null);

  const del = f.querySelector("#e-del");
  if (del) {
    del.onclick = () => {
      if (confirm(`Delete “${m.title}”? This removes it for the whole team.`)) onDone({ __delete: true, id: m.id });
    };
  }

  f.querySelector("#e-save").onclick = () => {
    const title = f.querySelector("#e-title").value.trim();
    if (!title) {
      f.querySelector("#e-title").focus();
      return;
    }
    onDone({
      id: m.id || "u" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
      cls: f.querySelector("#e-cls").value,
      title,
      kind: f.querySelector("#e-kind").value,
      due: f.querySelector("#e-due").value,
      dueTime: f.querySelector("#e-time").value.trim(),
      effort: f.querySelector("#e-eff").value,
      status: f.querySelector("#e-status").value,
      owners: [...owners],
      lead,
      second,
      note: f.querySelector("#e-note").value.trim(),
      window: m.window || "",
    });
  };

  f.querySelector("#e-title").focus();
}
